import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild, AfterViewInit } from '@angular/core';
import { SelectionModel } from '@angular/cdk/collections';
import { StudyItem } from '../../../services/studies-api.service';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Modality } from '../../../services/move.service';
import { StudyShare, StudyShareService, getShareDisplayStatus } from '../../../services/study-share.service';

@Component({
    selector: 'app-studies-results-table',
    templateUrl: './studies-results-table.component.html',
    styleUrls: ['./studies-results-table.component.scss'],
    animations: [
        trigger('detailExpand', [
            state('collapsed', style({ height: '0px', minHeight: '0' })),
            state('expanded', style({ height: '*' })),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)'))
        ])
    ],
    standalone: false
})
export class StudiesResultsTableComponent implements OnChanges, AfterViewInit {
  @Input() studies: StudyItem[] = [];
  @Input() isLoading = false;
  @Input() activeFiltersDescription: string = '';
  // Pagination Inputs
  @Input() hasNextPage = false;
  @Input() hasPreviousPage = false; // We track history in parent
  @Input() pageSize = 50;
  @Input() totalCount: number = 0; // Optional if we have it
  @Input() availableModalities: Modality[] = []; // Available modalities for C-MOVE
  @Input() showSendAction = true; // When false, SEND button is hidden
  @Input() showViewDownloadActions = true; // When false, View and Download buttons are hidden (e.g. SEND mode)
  @Input() showSendUnavailableHint = false; // When true, show help icon (target facility not available)
  /** When true, show a single "Send…" button that opens the target+modality dialog instead of the modality menu. */
  @Input() showSendDialog = false;
  /** Map of DICOM patientId → existence check result. null=checking, true=exists, false=not found. */
  @Input() patientExistsMap: Record<string, boolean | null> = {};
  /** When true, hide the Add Patient button (Spoke users cannot create patients). */
  @Input() isSpokeUser = false;
  /** Map of DICOM studyInstanceUid → StudyShare. Used to show expiry status for Spoke users. */
  @Input() shareMap: Record<string, StudyShare | null> = {};
  /** Map of DICOM studyInstanceUid → latest StudyShare sent to the selected target. For Hub SEND mode. */
  @Input() hubShareMap: Record<string, StudyShare | null> = {};

  @Output() downloadSingle = new EventEmitter<StudyItem>();
  @Output() downloadMultiple = new EventEmitter<StudyItem[]>();
  @Output() limitChange = new EventEmitter<number>();
  @Output() nextPage = new EventEmitter<void>();
  @Output() previousPage = new EventEmitter<void>();
  @Output() rowClick = new EventEmitter<StudyItem>();
  // Orthanc capabilities
  @Output() deleteStudy = new EventEmitter<StudyItem>();
  @Output() deleteMultiple = new EventEmitter<StudyItem[]>();
  @Output() sendStudy = new EventEmitter<{ study: StudyItem; modality: string }>();
  @Output() sendMultiple = new EventEmitter<{ studies: StudyItem[]; modality: string }>();
  @Output() viewStudy = new EventEmitter<StudyItem>();
  @Output() filterByPatientId = new EventEmitter<string>();
  @Output() addPatient = new EventEmitter<StudyItem>();

  @ViewChild(MatSort) sort!: MatSort;

  displayedColumns: string[] = [
    'select',
    'studyDate',
    'patientName',
    'patientId',
    'accessionNumber',
    // 'modalities',
    'instances',
    'actions'
  ];
  dataSource = new MatTableDataSource<StudyItem>([]);
  selection = new SelectionModel<StudyItem>(true, []);
  expandedElement: StudyItem | null = null;

  pendingMarkDoneIds = new Set<string>();

  constructor(
    private snackBar: MatSnackBar,
    private studyShareService: StudyShareService,
  ) {}

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
    this.dataSource.sortingDataAccessor = (item, property) => {
      switch (property) {
        case 'studyDate': return item.studyDate || '';
        case 'patientName': return item.patientName || '';
        case 'patientId': return item.patientId || '';
        case 'accessionNumber': return item.accessionNumber || '';
        default: return (item as any)[property] || '';
      }
    };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.studies) {
      this.dataSource.data = this.studies || [];
      this.selection.clear();
    }
  }

  /** Whether the number of selected elements matches the total number of rows. */
  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  masterToggle() {
    this.isAllSelected() ? this.selection.clear() : this.dataSource.data.forEach(row => this.selection.select(row));
  }

  onDownloadSingle(event: MouseEvent, study: StudyItem) {
    event.stopPropagation();
    this.downloadSingle.emit(study);
  }

  onDeleteStudy(event: MouseEvent, study: StudyItem) {
    event.stopPropagation();
    this.deleteStudy.emit(study);
  }

  onSendStudy(event: MouseEvent, study: StudyItem, modality: string) {
    event.stopPropagation();
    this.sendStudy.emit({ study, modality });
  }

  onSendStudyDialog(event: MouseEvent, study: StudyItem) {
    event.stopPropagation();
    this.sendStudy.emit({ study, modality: '' });
  }

  onSendMultipleDialog(): void {
    if (this.selection.selected.length > 0) {
      this.sendMultiple.emit({ studies: this.selection.selected, modality: '' });
    }
  }

  onViewSingle(event: Event, study: StudyItem) {
    event.stopPropagation();
    this.viewStudy.emit(study);
  }

  onAddPatient(event: Event, study: StudyItem): void {
    event.stopPropagation();
    this.addPatient.emit(study);
  }

  onSendUnavailableClick(event: MouseEvent): void {
    event.stopPropagation();
    this.snackBar.open(
      'The target facility is not available at this moment. Please try again or contact NuageDx support.',
      'Close',
      { duration: 8000 }
    );
  }

  onDownloadSelected() {
    if (this.selection.selected.length > 0) {
      this.downloadMultiple.emit(this.selection.selected);
    }
  }

  onSendMultipleSelected(modality: string): void {
    if (this.selection.selected.length > 0) {
      this.sendMultiple.emit({ studies: this.selection.selected, modality });
    }
  }

  onDeleteSelected() {
    if (this.selection.selected.length > 0) {
      this.deleteMultiple.emit(this.selection.selected);
    }
  }

  onClearSelection() {
    this.selection.clear();
  }

  isUploadedToPacs(study: StudyItem): boolean {
    return this.shareMap[study.studyInstanceUid]?.status === 'uploaded_to_pacs';
  }

  canMarkDone(study: StudyItem): boolean {
    if (!this.isSpokeUser) return false;
    const share = this.shareMap[study.studyInstanceUid];
    if (!share) return false;
    if (share.status === 'uploaded_to_pacs') return false;
    if (share.status === 'deleted' || share.status === 'deletion_failed') return false;
    return true;
  }

  onMarkDone(event: Event, study: StudyItem): void {
    event.stopPropagation();
    const share = this.shareMap[study.studyInstanceUid];
    if (!share || this.pendingMarkDoneIds.has(share.id)) return;

    this.pendingMarkDoneIds.add(share.id);
    this.studyShareService.markUploadedToPacs(share.id)
      .then(() => {
        this.snackBar.open('Study marked as Uploaded to PACS.', 'OK', { duration: 4000 });
      })
      .catch(err => {
        console.error('[StudiesResultsTable] markUploadedToPacs failed:', err);
        this.snackBar.open('Failed to mark study. Please try again.', 'Dismiss', { duration: 5000 });
      })
      .finally(() => {
        this.pendingMarkDoneIds.delete(share.id);
      });
  }

  /** Returns true when a Spoke study's share has expired and DICOM data has been removed (or is pending removal). */
  isShareExpired(study: StudyItem): boolean {
    if (!this.isSpokeUser || !study?.studyInstanceUid) return false;
    const share = this.shareMap[study.studyInstanceUid];
    if (!share) return false;
    // Check Firestore status field (set by Cloud Scheduler after deletion)
    const s = share.status;
    if (s === 'expired' || s === 'deleted' || s === 'deletion_failed') return true;
    // Also block if the TTL timestamp has passed but the deletion job hasn't run yet —
    // same condition that drives the "Expired" badge via getShareDisplayStatus().
    return getShareDisplayStatus(share) === 'expired';
  }

  /** Returns a relative "X ago" label for when the study was last sent to the target facility. */
  formatHubShareAge(study: StudyItem): string {
    const share = this.hubShareMap[study.studyInstanceUid];
    if (!share?.shared_at) return '';
    const ms = Date.now() - share.shared_at.toDate().getTime();
    const totalMinutes = Math.floor(ms / 60_000);
    const hours = Math.floor(totalMinutes / 60);
    const days = Math.floor(hours / 24);
    if (days >= 2) return `Sent ${days}d ago`;
    if (days === 1) return 'Sent yesterday';
    if (hours >= 1) return `Sent ${hours}h ago`;
    if (totalMinutes >= 1) return `Sent ${totalMinutes}m ago`;
    return 'Sent just now';
  }

  /**
   * Format study date from YYYYMMDD or YYYY-MM-DD to YYYY-MM-DD
   */
  formatStudyDate(date?: string): string {
    if (!date) return '';
    // If already in YYYY-MM-DD format, return as is
    if (date.includes('-')) return date;
    // Convert YYYYMMDD to YYYY-MM-DD
    if (date.length === 8) {
      return `${date.substring(0, 4)}-${date.substring(4, 6)}-${date.substring(6, 8)}`;
    }
    return date;
  }

  /**
   * Format study time from HHMMSS to HH:MM:SS
   */
  formatStudyTime(time?: string): string {
    if (!time) return '';
    // If already formatted, return as is
    if (time.includes(':')) return time;
    // Convert HHMMSS to HH:MM:SS
    if (time.length >= 6) {
      const hours = time.substring(0, 2);
      const minutes = time.substring(2, 4);
      const seconds = time.substring(4, 6);
      return `${hours}:${minutes}:${seconds}`;
    }
    return time;
  }

  /**
   * Format patient birth date from YYYYMMDD to YYYY-MM-DD
   */
  formatPatientBirthDate(date?: string): string {
    if (!date) return '';
    // If already in YYYY-MM-DD format, return as is
    if (date.includes('-')) return date;
    // Convert YYYYMMDD to YYYY-MM-DD
    if (date.length === 8) {
      return `${date.substring(0, 4)}-${date.substring(4, 6)}-${date.substring(6, 8)}`;
    }
    return date;
  }

  /**
   * Format patient name from DICOM format (LAST^FIRST^MIDDLE) to readable format
   */
  formatPatientName(name?: string): string {
    if (!name) return '';
    // DICOM format uses ^ as delimiter: LAST^FIRST^MIDDLE
    const parts = name.split('^');
    if (parts.length >= 2) {
      const lastName = parts[0] || '';
      const firstName = parts[1] || '';
      const middleName = parts[2] || '';
      const nameParts = [firstName, middleName, lastName].filter(p => p.trim() !== '');
      return nameParts.join(' ');
    }
    return name;
  }

  /**
   * True when all results belong to the same patient (same patientId).
   * Used to show/hide the Patient Information section in the expanded detail.
   */
  get isSinglePatient(): boolean {
    if (!this.studies || this.studies.length === 0) return false;
    const firstId = this.studies[0].patientId;
    return this.studies.every(s => s.patientId === firstId);
  }

  /**
   * Convert a value to string (helper for templates)
   */
  toString(value: any): string {
    return value != null ? String(value) : '';
  }

  /**
   * Copy text to clipboard and show notification
   */
  copyToClipboard(text: string, event: MouseEvent): void {
    event.stopPropagation();
    if (!text) return;

    navigator.clipboard
      .writeText(text)
      .then(() => {
        this.snackBar.open('Copied to clipboard', '', { duration: 2000 });
      })
      .catch(err => {
        console.error('Failed to copy text:', err);
        this.snackBar.open('Failed to copy to clipboard', '', { duration: 2000 });
      });
  }
}
